import { useEffect, useRef } from "react";
import { isSameYMD } from "../../calendar-view/drag.js";
import { toLocalDate } from "../../task-form/time-helpers.js";
import { useMobileSheetDraftStore } from "../../../stores/index.js";

/** The slice of `useTaskForm` the on-grid ghost mirrors + drives. */
export interface GhostSyncForm {
  open: boolean;
  editing: { id: string } | null;
  dateStr: string;
  hour: number | null;
  minute: number | null;
  allDay: boolean;
  duration: number;
  isEvent: boolean;
  title: string;
  setDateStr: (s: string) => void;
  setHour: (h: number | null) => void;
  setMinute: (m: number | null) => void;
  setDuration: (d: number) => void;
}

/** Timed create AND edit drafts get the on-grid ghost; all-day/undated don't. */
export function isGhostSyncEligible(
  form: Pick<GhostSyncForm, "open" | "allDay" | "dateStr" | "hour" | "minute">,
): boolean {
  return form.open && !form.allDay && !!form.dateStr && form.hour != null && form.minute != null;
}

interface GhostDraftBridge {
  active: boolean;
  day: Date | null;
  startMin: number;
  durationMin: number;
  title: string;
  isEvent: boolean;
  sourceId: string | null;
  publish: (draft: { day: Date; startMin: number; durationMin: number; title: string; isEvent: boolean; sourceId: string | null }) => void;
  clear: () => void;
}

export function applyFormToDraftBridge(
  store: GhostDraftBridge,
  form: Pick<GhostSyncForm, "open" | "editing" | "dateStr" | "hour" | "minute" | "allDay" | "duration" | "title" | "isEvent">,
): void {
  if (!isGhostSyncEligible(form)) {
    if (store.active) store.clear();
    return;
  }

  const startMin = (form.hour ?? 0) * 60 + (form.minute ?? 0);
  const day = new Date(`${form.dateStr}T00:00`);
  const sourceId = form.editing?.id ?? null;
  if (
    !store.active ||
    !store.day ||
    !isSameYMD(store.day, day) ||
    store.startMin !== startMin ||
    store.durationMin !== form.duration ||
    store.title !== form.title ||
    store.isEvent !== form.isEvent ||
    store.sourceId !== sourceId
  ) {
    store.publish({ day, startMin, durationMin: form.duration, title: form.title, isEvent: form.isEvent, sourceId });
  }
}

/**
 * Keeps the on-grid draggable ghost in sync with the sheet's create-mode
 * schedule. The sheet publishes form state into the bridge store; the ghost
 * writes date/time edits back through the same bridge.
 */
export function useGhostSync(form: GhostSyncForm): void {
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    applyFormToDraftBridge(useMobileSheetDraftStore.getState(), form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.open, form.editing, form.allDay, form.dateStr, form.hour, form.minute, form.duration, form.title, form.isEvent]);

  useEffect(() => {
    return useMobileSheetDraftStore.subscribe((s, prev) => {
      // Ghost-originated moves only. `publish` bounds what it stores to the day,
      // so echoing every store change back would rewrite the form's schedule at
      // LOAD time for anything that doesn't fit (a block running past midnight,
      // a zero-length one) — an edit the user never made.
      if (s.gesture === prev.gesture) return;
      if (!s.active || !s.day) return;
      const f = formRef.current;
      const nextDate = toLocalDate(s.day);
      if (nextDate !== f.dateStr) f.setDateStr(nextDate);
      const h = Math.floor(s.startMin / 60);
      const m = s.startMin % 60;
      if (h !== f.hour) f.setHour(h);
      if (m !== f.minute) f.setMinute(m);
      if (s.durationMin !== f.duration) f.setDuration(s.durationMin);
    });
  }, []);

  useEffect(() => () => useMobileSheetDraftStore.getState().clear(), []);
}
